/**
 * Round a value up to a "nice" number for clean axis tick labels.
 * - value < 1: round to 1 decimal place (0.3 → 0.3, 0.77 → 0.8)
 * - value < 30: round to next integer (16.3 → 17)
 * - value ≥ 30: round to next multiple of 5 (32 → 35, 48 → 50)
 */
export function ceilToNice(value: number): number {
  if (value <= 0) return 1;
  if (value < 1) return Math.ceil(value * 10) / 10;
  if (value < 30) return Math.ceil(value);
  return Math.ceil(value / 5) * 5;
}

/**
 * Compute symmetric Y-axis bounds mirrored about zero.
 *
 * The dilation angle chart must read so that 0° sits at the vertical centre
 * of the plot area — engineers compare contraction (negative) vs dilation
 * (positive) at a glance, and an axis biased to one side hides that. The
 * axis runs from `-X` to `+X`, where `X = ceilToNice(max(|dataMin|,
 * |dataMax|))`. The data may sit entirely in the lower or upper half — that
 * is intentional, NOT uneven whitespace.
 *
 * Examples (user spec: "Same value for min and max but +-, e.g. 8 to -8"):
 *   data [-11.26, 0]   → absMax 11.26 → ceilToNice 12 → axis −12..+12
 *   data [-6, -1]      → absMax 6     → ceilToNice 6  → axis  −6..+6
 *   data [5, 10]       → absMax 10    → ceilToNice 10 → axis −10..+10
 *   data [0.3, 0.77]   → absMax 0.77  → ceilToNice 0.8 → axis −0.8..+0.8
 *
 * @param values - Array of data values
 * @returns { min, max } with min = -nice, max = +nice
 */
export function symmetricAxisBounds(values: number[]): { min: number; max: number } {
  if (values.length === 0) return { min: -1, max: 1 };

  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  const absMax = Math.max(Math.abs(dataMin), Math.abs(dataMax));
  const nice = ceilToNice(absMax);

  return { min: -nice, max: nice };
}
